/* eslint-disable camelcase */
import { useEffect, useState } from 'react';

import { skadetypeToString } from './meldskade/reducer/skadetype';
import { CurrentSteg, CurrentStegTypes, OppsummeringSteg, SporsmalSteg } from './sporsmalsflyt/reducer/current-steg';
import { Skademeldingstype } from './types/generated-types';

declare global {
    interface Window {
        dataLayer?: any[];
    }
}

interface RegisterAnalyticsEvent {
    event_action: string;
    event_label: string;
}

const registerAnalyticsEvent = ({ event_action, event_label }: RegisterAnalyticsEvent) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: 'forsikring_skademelding',
        event_category: 'Forsikring skademelding',
        event_action,
        event_label,
    });
};

const registerAnalyticsEventStart = (skadetype: Skademeldingstype) => {
    registerAnalyticsEvent({
        event_action: 'Start',
        event_label: `skadetype:${skadetypeToString(skadetype)}`,
    });
};

const registerAnalyticsEventSteg = (skadetype: Skademeldingstype, sporsmalId: string) => {
    registerAnalyticsEvent({
        event_action: 'Steg',
        event_label: `skadetype:${skadetypeToString(skadetype)};sporsmal_id:${sporsmalId}`,
    });
};

export const registerAnalyticsEventStegFullfort = (skadetype: Skademeldingstype, sporsmalId: string) => {
    registerAnalyticsEvent({
        event_action: 'Steg fullfort',
        event_label: `skadetype:${skadetypeToString(skadetype)};sporsmal_id:${sporsmalId}`,
    });
};

export const registerAnalyticsEventSendInn = (skadetype: Skademeldingstype) => {
    registerAnalyticsEvent({
        event_action: 'Send inn',
        event_label: `skadetype:${skadetypeToString(skadetype)}`,
    });
};

// HOOKS

const useAnalyticsStartSporsmalsflyt = (skadetype: Skademeldingstype, currentSteg: CurrentSteg) => {
    const [hasSentStartEvent, setHasSentStartEvent] = useState(false);

    useEffect(() => {
        if (!hasSentStartEvent && currentSteg.type === CurrentStegTypes.Sporsmal) {
            registerAnalyticsEventStart(skadetype);
            setHasSentStartEvent(true);
        }
    }, [hasSentStartEvent, skadetype, currentSteg]);
};

/* Oppsummering er et hardkodet spørsmål, og har derfor ikke noen egen id.
 * Vi vil allikevel registrere en event for at man har kommet til oppsummering,
 * og bruker derfor dummy-iden under.
 */
const OPPSUMMERING_ID = 'Oppsummering';

const hasSentSporsmalStegEvent = (
    currentSteg: CurrentSteg,
    registrerteSporsmalsIder: string[],
): currentSteg is SporsmalSteg =>
    currentSteg.type === CurrentStegTypes.Sporsmal &&
    !registrerteSporsmalsIder.includes(currentSteg.currentSporsmal.sporsmal.id);

const hasSentOppsummeringStegEvent = (
    currentSteg: CurrentSteg,
    registrerteSporsmalsIder: string[],
): currentSteg is OppsummeringSteg =>
    currentSteg.type === CurrentStegTypes.Oppsummering && !registrerteSporsmalsIder.includes(OPPSUMMERING_ID);

const useAnalyticsSteg = (skadetype: Skademeldingstype, currentSteg: CurrentSteg) => {
    const [registrerteSporsmalsIder, setRegistrerteSporsmalsIder] = useState<string[]>([]);

    useEffect(() => {
        if (hasSentSporsmalStegEvent(currentSteg, registrerteSporsmalsIder)) {
            registerAnalyticsEventSteg(skadetype, currentSteg.currentSporsmal.sporsmal.id);
            setRegistrerteSporsmalsIder([...registrerteSporsmalsIder, currentSteg.currentSporsmal.sporsmal.id]);
        }
        if (hasSentOppsummeringStegEvent(currentSteg, registrerteSporsmalsIder)) {
            registerAnalyticsEventSteg(skadetype, OPPSUMMERING_ID);
            setRegistrerteSporsmalsIder([...registrerteSporsmalsIder, OPPSUMMERING_ID]);
        }
    }, [registrerteSporsmalsIder, setRegistrerteSporsmalsIder, skadetype, currentSteg]);
};

export const useAnalytics = (skadetype: Skademeldingstype, currentSteg: CurrentSteg) => {
    useAnalyticsStartSporsmalsflyt(skadetype, currentSteg);
    useAnalyticsSteg(skadetype, currentSteg);
};
